/**
 * QuantityIncrementer is a component with a decrement button, an input, and an increment button. They all modify the same value (item.quantity).
 *  @param {object} item - The item that's being modified.
 *  @param {string} item.id - Unique identifier for the item
 *  @param {integer} item.quantity - The quantity of the item. This is the value you are modifying with this component.
 *  @param {string} item.validationClasses - Optional. Any classes you want to pass in to the Input field to indicate whether the input is valid.
 *  @param {function} item.handleInputQuantity - A callback function that handles updating the value of the item via the typed in input. Can handle decimals and precision.
 *  @param {function} item.handleIncrementQuantity - A callback function that handles updating the value of the item in whole number increments.
 */
/**
 * This callback is used to update the input quantity via the text input.
 * @callback handleInputQuantity
 * @param {string} quantity = The stringified new quantity for the item, eg "4.55".
 */
/**
 * This callback is used to update the input quantity via the incrementer buttons.
 * @callback handleIncrementQuantity
 * @param {int} quantity = The new quantity for the item
 */
import LegacyButton from "legacy/LegacyButton"
import PropTypes from "prop-types"
import React from "react"

class QuantityIncrementer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
    this.handleIncrement = this.handleIncrement.bind(this)
    this.renderIncrementButton = this.renderIncrementButton.bind(this)
    this.renderQuantityInput = this.renderQuantityInput.bind(this)
  }

  handleIncrement(quantity) {
    return this.props.handleIncrementQuantity(Math.round(quantity))
  }

  renderIncrementButton({
    className,
    disabled,
    ariaLabel,
    iconName,
    iconStyle = "kit",
    handleIncrement,
  }) {
    return (
      <LegacyButton
        className={`btn-sm ${className}`}
        disabled={disabled}
        type="tertiary"
        aria-label={ariaLabel}
        onClick={handleIncrement}
        icon={`${iconName} text-sm`}
        iconStyle={iconStyle}
        iconOnly={true}
      />
    )
  }

  renderQuantityInput() {
    return (
      <input
        className={`w-8 bg-transparent text-center ${
          this.props.validationClasses || ""
        }`}
        type="number"
        min="1"
        onChange={(event) => this.props.handleInputQuantity(event.target.value)}
        id={this.props.item.id}
        value={this.props.item.quantity}
        aria-label="quantity"
      />
    )
  }

  render() {
    return (
      <div className="flex rounded bg-gray-100">
        {this.renderIncrementButton({
          className: `border ${
            this.props.item.quantity <= 0
              ? ""
              : "hover:bg-red-100 hover:text-red-700"
          }`,
          disabled: this.props.item.quantity <= 0,
          ariaLabel: "subtract from cart",
          iconName: "fa-window-minimize",
          iconStyle: "solid",
          handleIncrement: () =>
            this.handleIncrement(this.props.item.quantity - 1),
        })}
        {this.renderQuantityInput()}
        {this.renderIncrementButton({
          className: "hover:bg-teal-100 hover:text-teal-800",
          disabled: false,
          ariaLabel: "add to cart",
          iconName: "fa-plus",
          iconStyle: "solid",
          handleIncrement: () =>
            this.handleIncrement(this.props.item.quantity + 1),
        })}
      </div>
    )
  }
}

QuantityIncrementer.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  }).isRequired,
  handleInputQuantity: PropTypes.func.isRequired,
  handleIncrementQuantity: PropTypes.func.isRequired,
  validationClasses: PropTypes.string,
}

export default QuantityIncrementer
